import { Head, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, FileText, Filter } from "lucide-react"
import { useState } from "react"

import Heading from "@/components/heading"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { auditsPath } from "@/routes"
import type { Audit, BreadcrumbItem, User } from "@/types"

interface AuditsIndexProps {
  audits: Audit[]
  users: Pick<User, "id" | "name" | "email">[]
  resource_types: string[]
  filters: {
    resource_type?: string
    user_id?: string
    audit_action?: string
    start_date?: string
    end_date?: string
  }
  pagination: {
    page: number
    per_page: number
    total: number
  }
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Audit Log", href: auditsPath() },
]

const ACTION_LABELS: Record<string, string> = {
  create: "Created",
  update: "Updated",
  destroy: "Deleted",
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatChanges(changes: Record<string, unknown>): string {
  const entries = Object.entries(changes)
  if (entries.length === 0) return "No changes"

  return entries
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}: ${value[0] || "(empty)"} → ${value[1] || "(empty)"}`
      }
      return `${key}: ${value}`
    })
    .join(", ")
}

export default function AuditsIndex({
  audits,
  users,
  resource_types,
  filters,
  pagination,
}: AuditsIndexProps) {
  const [showFilters, setShowFilters] = useState(
    Object.keys(filters).length > 0,
  )

  function handleFilter(newFilters: Record<string, string>) {
    router.get(
      auditsPath(),
      { ...filters, ...newFilters, page: 1 },
      { preserveState: true },
    )
  }

  function handlePageChange(page: number) {
    router.get(auditsPath(), { ...filters, page }, { preserveState: true })
  }

  function clearFilters() {
    router.get(auditsPath())
  }

  const totalPages = Math.ceil(pagination.total / pagination.per_page)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Audit Log" />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <Heading
            title="Audit Log"
            description="Track all changes made to the system"
          />
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <div className="grid gap-2">
                  <Label htmlFor="resource_type">Resource Type</Label>
                  <select
                    id="resource_type"
                    value={filters.resource_type || ""}
                    onChange={(e) =>
                      handleFilter({ resource_type: e.target.value })
                    }
                    className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    <option value="">All types</option>
                    {resource_types.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="user_id">User</Label>
                  <select
                    id="user_id"
                    value={filters.user_id || ""}
                    onChange={(e) => handleFilter({ user_id: e.target.value })}
                    className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    <option value="">All users</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="audit_action">Action</Label>
                  <select
                    id="audit_action"
                    value={filters.audit_action || ""}
                    onChange={(e) =>
                      handleFilter({ audit_action: e.target.value })
                    }
                    className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    <option value="">All actions</option>
                    <option value="create">Create</option>
                    <option value="update">Update</option>
                    <option value="destroy">Delete</option>
                  </select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="start_date">From Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={filters.start_date || ""}
                    onChange={(e) =>
                      handleFilter({ start_date: e.target.value })
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="end_date">To Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={filters.end_date || ""}
                    onChange={(e) => handleFilter({ end_date: e.target.value })}
                  />
                </div>
              </div>

              {Object.keys(filters).length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="mt-4"
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Audit Entries */}
        <div className="mt-6">
          {audits.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="text-muted-foreground h-12 w-12" />
                <h3 className="mt-4 text-lg font-semibold">No audit entries</h3>
                <p className="text-muted-foreground mt-2 text-sm">
                  {Object.keys(filters).length > 0
                    ? "No entries match your filters"
                    : "System changes will appear here"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {audits.map((audit) => (
                <Card key={audit.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              audit.action === "create"
                                ? "default"
                                : audit.action === "destroy"
                                  ? "destructive"
                                  : "secondary"
                            }
                          >
                            {ACTION_LABELS[audit.action] || audit.action}
                          </Badge>
                          <span className="font-medium">
                            {audit.auditable_type} #{audit.auditable_id}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-sm">
                          {formatChanges(audit.audited_changes)}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-medium">
                          {audit.user?.name || "System"}
                        </div>
                        <div className="text-muted-foreground">
                          {formatDate(audit.created_at)}
                        </div>
                        {audit.remote_address && (
                          <div className="text-muted-foreground text-xs">
                            {audit.remote_address}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-muted-foreground text-sm">
                    Showing {(pagination.page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.per_page,
                      pagination.total,
                    )}{" "}
                    of {pagination.total} entries
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
